import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../config';

const ManageProposalHotels = () => {
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddHotel, setShowAddHotel] = useState(false);
  const [googleMapsLink, setGoogleMapsLink] = useState('');
  const [parsedHotel, setParsedHotel] = useState(null);
  
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/proposals');
      return;
    }
    fetchProposal();
  }, [id, user]);

  const fetchProposal = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/proposals/${id}`);
      setProposal(response.data.data);
    } catch (err) {
      setError('Failed to load proposal');
      console.error('Error fetching proposal:', err);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced Google Maps URL parsing with shortened URL support
  const parseGoogleMapsLink = (url) => {
    try {
      // Check for shortened URLs first
      const shortenedDomains = ['goo.gl', 'bit.ly', 'tinyurl.com', 't.co', 'tiny.cc'];
      const urlObj = new URL(url);
      
      if (shortenedDomains.some(domain => urlObj.hostname.includes(domain))) {
        return {
          name: 'Shortened URL Detected',
          location: 'Please use the full Google Maps URL',
          googleMapsLink: url,
          isShortened: true,
          error: 'Shortened URLs cannot be automatically parsed. Please copy the full Google Maps URL after the page loads.'
        };
      }

      if (!url || (!url.includes('google.com/maps') && !url.includes('maps.google.com'))) {
        return null;
      }

      // Handle various Google Maps URL formats
      const urlParts = url.split('/');
      const placeIndex = urlParts.findIndex(part => part === 'place');
      
      let hotelName = '';
      let location = '';
      
      if (placeIndex !== -1 && urlParts[placeIndex + 1]) {
        const encodedName = urlParts[placeIndex + 1];
        
        // Handle different encoding formats
        hotelName = decodeURIComponent(encodedName)
          .replace(/\+/g, ' ')
          .replace(/-/g, ' ')
          .replace(/,/g, ', ')
          .replace(/_/g, ' ')
          .trim();
          
        // Clean up common Google Maps prefixes/suffixes and hotel types
        hotelName = hotelName
          .replace(/^hotel\s+/i, '')
          .replace(/^resort\s+/i, '')
          .replace(/^boutique\s+/i, '')
          .replace(/^luxury\s+/i, '')
          .replace(/^business\s+/i, '')
          .replace(/^spa\s+/i, '')
          .replace(/\s+hotel$/i, '')
          .replace(/\s+resort$/i, '')
          .replace(/\s+boutique$/i, '')
          .replace(/\s+luxury$/i, '')
          .replace(/\s+business$/i, '')
          .replace(/\s+spa$/i, '')
          .replace(/\s+inn$/i, '')
          .replace(/\s+lodge$/i, '')
          .trim();
          
        // Capitalize first letter of each word
        hotelName = hotelName.replace(/\b\w/g, l => l.toUpperCase());
      }

      // Extract location from multiple sources
      const queryParams = new URLSearchParams(url.split('?')[1] || '');
      const query = queryParams.get('q') || '';
      
      if (query) {
        // Try to extract location from query parameter
        const parts = query.split(',');
        if (parts.length >= 2) {
          const city = parts[parts.length - 2].trim();
          const country = parts[parts.length - 1].trim();
          location = `${city}, ${country}`;
        } else if (parts.length === 1) {
          location = parts[0].trim();
        }
      } else if (urlParts.length > placeIndex + 2) {
        // Try to extract from URL path
        const locationPart = urlParts[placeIndex + 2];
        if (locationPart && !locationPart.includes('@') && !locationPart.includes('data=')) {
          location = decodeURIComponent(locationPart)
            .replace(/\+/g, ' ')
            .replace(/_/g, ' ')
            .trim();
        }
      }

      // Try to extract from coordinates if available
      const coordsMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (!location && coordsMatch) {
        // We could use reverse geocoding here, but for now just indicate it's a coordinates-based location
        location = `Coordinates: ${coordsMatch[1]}, ${coordsMatch[2]}`;
      }

      // Clean up location
      if (location) {
        location = location
          .replace(/^\s*,\s*/, '') // Remove leading comma
          .replace(/\s+/g, ' ') // Normalize spaces
          .replace(/,$/, '') // Remove trailing comma
          .trim();
          
        // Capitalize location
        location = location.replace(/\b\w/g, l => l.toUpperCase());
      }

      // Final validation
      if (!hotelName || hotelName === 'Hotel Name') {
        return {
          name: 'Could not extract hotel name',
          location: location || 'Location not found',
          googleMapsLink: url,
          error: 'Please ensure the URL contains a hotel name. Try using the full Google Maps URL.'
        };
      }

      return {
        name: hotelName,
        location: location || 'Location not found',
        googleMapsLink: url
      };
    } catch (error) {
      console.error('Error parsing Google Maps URL:', error);
      return {
        name: 'Invalid URL format',
        location: 'Please check the URL',
        googleMapsLink: url,
        error: 'Please provide a valid Google Maps URL'
      };
    }
  };

  const handleGoogleMapsLinkChange = (e) => {
    const url = e.target.value.trim();
    setGoogleMapsLink(url);
    
    if (url) {
      const parsed = parseGoogleMapsLink(url);
      setParsedHotel(parsed);
      
      // Clear any previous errors
      setError('');
    } else {
      setParsedHotel(null);
    }
  };

  const handleAddHotel = async () => {
    if (!googleMapsLink.trim()) {
      setError('Please enter a Google Maps link');
      return;
    }

    const parsed = parseGoogleMapsLink(googleMapsLink);
    if (!parsed) {
      setError('Invalid Google Maps link. Please check the URL.');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/proposals/${id}/hotels`, {
        name: parsed.name,
        location: parsed.location,
        googleMapsLink: googleMapsLink.trim(),
        status: 'pending'
      });
      setProposal(response.data.data);
      setShowAddHotel(false);
      setGoogleMapsLink('');
      setParsedHotel(null);
      setError('');
    } catch (err) {
      setError('Failed to add hotel');
      console.error('Error adding hotel:', err);
    }
  };

  const handleDeleteHotel = async (hotelId) => {
    if (window.confirm('Are you sure you want to delete this hotel?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/proposals/${id}/hotels/${hotelId}`);
        fetchProposal();
      } catch (err) {
        setError('Failed to delete hotel');
        console.error('Error deleting hotel:', err);
      }
    }
  };

  if (loading) return <div>Loading proposal...</div>;
  if (error) return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;
  if (!proposal) return <div style={{ padding: '20px' }}>Proposal not found</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            className="back-button"
            onClick={() => navigate('/proposals')}
          >
            &lt;
          </button>
          <h1>Manage Hotels - {proposal.title}</h1>
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => navigate(`/proposals/${id}/preview`)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            Preview as Client
          </button>
          <button
            onClick={() => setShowAddHotel(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            Add Hotel
          </button>
        </div>
      </div>

      {/* Hotels List */}
      <div style={{ display: 'grid', gap: '15px' }}>
        {proposal.hotels && proposal.hotels.length > 0 ? (
          proposal.hotels.map((hotel, index) => (
            <div 
              key={hotel._id || index} 
              style={{
                border: '1px solid #ddd',
                borderRadius: '5px',
                padding: '15px',
                backgroundColor: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <h3 style={{ margin: '0 0 5px 0' }}>{hotel.name}</h3>
                {hotel.location && (
                  <p style={{ margin: '0', color: '#666' }}>📍 {hotel.location}</p>
                )}
                {hotel.googleMapsLink && (
                  <p style={{ margin: '5px 0 0 0' }}>
                    <a 
                      href={hotel.googleMapsLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'none' }}
                    >
                      View on Maps →
                    </a>
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '3px',
                  color: 'white',
                  backgroundColor: hotel.status === 'accepted' ? '#28a745' :
                                 hotel.status === 'rejected' ? '#dc3545' : '#6c757d',
                  fontSize: '0.75em',
                  textTransform: 'uppercase'
                }}>
                  {hotel.status}
                </span>
                <button
                  onClick={() => handleDeleteHotel(hotel._id)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '0.7em'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
            No hotels added yet. Click "Add Hotel" to get started.
          </div>
        )}
      </div>

      {/* Add Hotel Modal */}
      {showAddHotel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '5px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3>Add Hotel to Proposal</h3>
            
            {error && (
              <div style={{ 
                color: 'red', 
                marginBottom: '15px', 
                padding: '10px', 
                backgroundColor: '#f8d7da', 
                border: '1px solid #f5c6cb', 
                borderRadius: '3px' 
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'grid', gap: '15px', marginTop: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Google Maps Link *
                </label>
                <input
                  type="url"
                  value={googleMapsLink}
                  onChange={handleGoogleMapsLinkChange}
                  placeholder="https://maps.google.com/maps/place/Hotel+Name/..."
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '3px' }}
                  required
                />
                <small style={{ color: '#666', fontSize: '0.8em', display: 'block', marginTop: '5px' }}>
                  Paste a Google Maps link to automatically extract hotel information
                </small>
              </div>
              
              {parsedHotel && (
                <div style={{
                  padding: '15px',
                  backgroundColor: parsedHotel.error ? '#f8d7da' : '#d4edda',
                  borderRadius: '3px',
                  border: `1px solid ${parsedHotel.error ? '#f5c6cb' : '#c3e6cb'}`
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: parsedHotel.error ? '#721c24' : '#155724' }}>
                    {parsedHotel.isShortened ? '⚠️ Shortened URL Detected' : 
                     parsedHotel.error ? '❌ Parsing Issue' : 
                     '✅ Information Extracted'}
                  </h4>
                  
                  {parsedHotel.isShortened ? (
                    <div style={{ color: '#721c24' }}>
                      <p style={{ margin: '0 0 10px 0' }}>
                        <strong>Shortened URLs cannot be automatically parsed.</strong>
                      </p>
                      <ol style={{ margin: '0', paddingLeft: '20px' }}>
                        <li>Click the shortened link to open the full Google Maps page</li>
                        <li>Copy the URL from your browser's address bar</li>
                        <li>Paste the full Google Maps URL here</li>
                      </ol>
                    </div>
                  ) : parsedHotel.error ? (
                    <div style={{ color: '#721c24' }}>
                      <p style={{ margin: '0' }}>{parsedHotel.error}</p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <div>
                        <strong>🏨 Hotel Name:</strong> {parsedHotel.name}
                      </div>
                      <div>
                        <strong>📍 Location:</strong> {parsedHotel.location}
                      </div>
                      <div>
                        <a 
                          href={parsedHotel.googleMapsLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#155724', textDecoration: 'none' }}
                        >
                          🔗 View on Google Maps →
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button 
                onClick={() => {
                  setShowAddHotel(false);
                  setGoogleMapsLink('');
                  setParsedHotel(null);
                  setError('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddHotel}
                disabled={!parsedHotel || parsedHotel.isShortened || parsedHotel.error}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (!parsedHotel || parsedHotel.isShortened || parsedHotel.error) ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: (!parsedHotel || parsedHotel.isShortened || parsedHotel.error) ? 'not-allowed' : 'pointer'
                }}
              >
                {parsedHotel?.isShortened ? 'Use Full URL' : 'Add Hotel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProposalHotels;
